// The pure logic of the PUBLIC_LEDGER team's "top up nodeStream's public ledger account" card
// (@ns/public-ledger-platform-top-up). Amounts are nodeStream credits: 1 credit = 10^6 tinybars, with no exchange rate.
import type { LedgerBalanceReading } from '../models/company.js';
import { isLedgerNetwork, type LedgerNetwork } from './public-ledger-network.js';
import { formatCredits, readingCredits } from './public-ledger-credits.js';

// The job metadata public-ledger-liquidity-check and public-ledger-confirm-platform-top-up write on the card
export const PLATFORM_TOP_UP_METADATA = {
  network: 'NETWORK',
  targetCredits: 'TARGET_CREDITS',
  requestedCredits: 'REQUESTED_CREDITS',
  enteredTotalCredits: 'ENTERED_TOTAL_CREDITS',
  // #25 phase 6 (round 13): the threshold the account must stay above, and the companies' fee top-ups waiting on it
  thresholdCredits: 'THRESHOLD_CREDITS',
  feesWaitingCredits: 'FEES_WAITING_CREDITS',
} as const;

// The ledger keeps 8 decimal places of its unit, so a credit has 6: an amount with more can't be a real balance
const CREDIT_DECIMALS = 6;
// The entered total is whole credits, as public-ledger-confirm-platform-top-up takes it; the 100-credit tolerance it allows
// covers rounding the account's balance down
const WHOLE_CREDITS = /^\d+$/;
const CREDITS = new RegExp(`^\\d+(\\.\\d{1,${CREDIT_DECIMALS}})?$`);

type Metadata = { key: string; value: string | null }[] | null | undefined;

const metadataValue = (metadata: Metadata, key: string) => metadata?.find((m) => m.key === key)?.value ?? null;

/**
 * A credit amount as the lambdas write it to job metadata, or null for none or anything that isn't a non-negative number.
 */
export function parseCredits(value: string | number | null | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null;
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return CREDITS.test(text) ? Number(text) : null;
}

export interface PlatformTopUpFigures {
  network: LedgerNetwork | null;
  // nodeStream's platform target, in credits (its ℏ target × 100)
  targetCredits: number | null;
  // What the team is asked to send: everything owed to companies plus (target − balance), as written on the card; with
  // nothing written yet, target − balance, never below 0
  requestedCredits: number | null;
  // The latest stored reading of the platform account, in credits
  balanceCredits: number | null;
  // The total the team last entered, if they have confirmed before
  enteredTotalCredits: number | null;
  // The threshold the account must stay above, in credits
  thresholdCredits: number | null;
  // What companies' public ledger fee top-ups waiting on the account come to, in credits; 0 when none
  feesWaitingCredits: number | null;
}

/** The card's figures, from its job metadata and the stored reading of the platform account. */
export function platformTopUpFigures(
  metadata: Metadata,
  reading: LedgerBalanceReading | null | undefined,
): PlatformTopUpFigures {
  const network = metadataValue(metadata, PLATFORM_TOP_UP_METADATA.network);
  const targetCredits = parseCredits(metadataValue(metadata, PLATFORM_TOP_UP_METADATA.targetCredits));
  const balanceCredits = readingCredits(reading);
  let requestedCredits = parseCredits(metadataValue(metadata, PLATFORM_TOP_UP_METADATA.requestedCredits));
  if (requestedCredits === null && targetCredits !== null && balanceCredits !== null) {
    requestedCredits = Math.max(0, targetCredits - balanceCredits);
  }
  return {
    network: isLedgerNetwork(network) ? network : null,
    targetCredits,
    requestedCredits,
    balanceCredits,
    enteredTotalCredits: parseCredits(metadataValue(metadata, PLATFORM_TOP_UP_METADATA.enteredTotalCredits)),
    thresholdCredits: parseCredits(metadataValue(metadata, PLATFORM_TOP_UP_METADATA.thresholdCredits)),
    feesWaitingCredits: parseCredits(metadataValue(metadata, PLATFORM_TOP_UP_METADATA.feesWaitingCredits)),
  };
}

// The card's intro (approved, round 12 follow-ups settled 2026-10-30), naming the network when the card has one
export function platformTopUpIntro(networkLabel: string | null): string {
  return (
    `nodeStream's public ledger account${networkLabel ? ` on ${networkLabel}` : ''} is running low. It pays for new ` +
    "companies' public ledger accounts, internal messages, nodeStream credit deliveries and companies' automatic payment " +
    'fee top-ups.'
  );
}

/**
 * The line the card shows while companies' public ledger fee top-ups wait on the account (approved, round 12 follow-ups
 * settled 2026-10-30): its balance, the threshold it must stay above and what to send. null when nothing waits or a figure
 * is missing.
 */
export function feesWaitingLine(figures: PlatformTopUpFigures): string | null {
  const { feesWaitingCredits, balanceCredits, thresholdCredits, requestedCredits } = figures;
  if (!feesWaitingCredits || balanceCredits === null || thresholdCredits === null || requestedCredits === null)
    return null;
  return (
    `Companies' public ledger fee top-ups are waiting because the platform account holds ${formatCredits(balanceCredits)} ` +
    `nodeStream credits, too few to send them and stay above ${formatCredits(thresholdCredits)}. Top it up by at least ` +
    `${formatCredits(requestedCredits)} credits.`
  );
}

export type EnteredTotal = { credits: number; error?: undefined } | { credits?: undefined; error: string };

/**
 * The total the team enters once they have topped the account up: the account's whole balance in credits, not the amount
 * sent. Group separators and spaces are ignored ("150,000"). It must be a whole number of credits, more than 0 and no larger
 * than the lambda takes: public-ledger-confirm-platform-top-up refuses anything else.
 */
export function parseEnteredTotal(input: string | null | undefined): EnteredTotal {
  const text = (input ?? '').replace(/[,\s]/g, '');
  if (!text) return { error: "Enter the account's new total, in credits" };
  if (CREDITS.test(text) && !WHOLE_CREDITS.test(text)) {
    return { error: 'Enter the total as a whole number of credits, rounded down, such as 150000' };
  }
  if (!WHOLE_CREDITS.test(text)) return { error: 'Enter the total as a number of credits, such as 150000' };
  const credits = Number(text);
  if (!(credits > 0)) return { error: 'The total must be more than 0 credits' };
  if (!Number.isSafeInteger(credits)) return { error: 'That total is too large. Check the number of credits' };
  return { credits };
}

/**
 * The platform account's reading in a public-ledger-read-balance answer, for the given network. The answer's `res.message`
 * is read leniently: `{balances: {PLATFORM: reading}}`, `{PLATFORM: reading}`, or the same keyed by network beneath
 * PLATFORM. Null when it holds no usable reading, so the card falls back to the stored one.
 */
export function platformReadingFromAnswer(
  message: unknown,
  network: LedgerNetwork | null,
): LedgerBalanceReading | null {
  const record = (value: unknown) => (value && typeof value === 'object' ? (value as Record<string, unknown>) : null);
  const answer = record(message);
  if (!answer) return null;
  const balances = record(answer.balances) ?? answer;
  let platform = record(balances.PLATFORM) ?? record(balances.platform);
  if (platform && network && !('tinybars' in platform)) platform = record(platform[network]);
  if (!platform || typeof platform.tinybars !== 'string') return null;
  if (network && platform.network && platform.network !== network) return null;
  return platform as LedgerBalanceReading;
}

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object';

/**
 * Whether a 200 answer from public-ledger-confirm-platform-top-up says the job was completed: `{completed: true}`,
 * `{status: 'COMPLETE'}`, or the job engine's list of actions that completing a job hands back. A refusal is a 4xx (thrown)
 * or an answer with `completed: false` and a message.
 */
export function confirmedComplete(answer: unknown): boolean {
  if (Array.isArray(answer)) return true;
  if (!isRecord(answer)) return false;
  return answer.completed === true || answer.status === 'COMPLETE';
}

// The sentence a 200 answer that did not complete the job gives, if any
export function confirmRefusal(answer: unknown): string | null {
  if (typeof answer === 'string' && answer.trim()) return answer;
  if (isRecord(answer) && typeof answer.message === 'string' && answer.message.trim()) return answer.message;
  return null;
}
